import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function HowWeWork() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="how-we-work" className="relative w-full py-32 md:py-48 overflow-hidden flex items-center justify-center">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1443870/img/university_campus_bridge.png)] bg-cover bg-center" />

      {}
      <div className="absolute inset-0 bg-primary/60" />

      {}
      <div ref={ref} className={`relative z-10 container mx-auto px-4 flex flex-col items-center justify-center transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <h2 className="sr-only">How We Work</h2>
        <Link to="/HowWeWorkPage">
          <Button size="lg" className="bg-background text-primary hover:bg-background/90 text-lg px-10 py-8 rounded-full shadow-2xl animate-pulse hover:animate-none transition-all border border-border/20">
            See How We Work
            <ArrowRight className="ml-2 size-5" />
          </Button>
        </Link>
      </div>
    </section>;
}